import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { getFirestore, doc, getDoc } from 'firebase/firestore'; 
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
// Normaliza estadoLista a los dos valores canónicos: 'Activo' | 'Espera'.
// Tolera datos legacy/inconsistentes del backend ('En espera', 'Activa', etc.).
const normalizarEstadoLista = (estado) => {
if (typeof estado !== 'string') return estado;
const e = estado.trim().toLowerCase();
if (e.includes('espera')) return 'Espera';
if (e.startsWith('activ')) return 'Activo';
return estado;
};
// Convierte fechaExpiracion (Firestore Timestamp | string ISO | Date) a Date, o null.
const aFecha = (valor) => {
if (!valor) return null;
if (typeof valor.toDate === 'function') return valor.toDate();
if (valor.seconds != null) return new Date(valor.seconds * 1000);
const d = new Date(valor);
return isNaN(d.getTime()) ? null : d;
};
// Un cupo 'Activo' con fechaExpiracion vencida se trata como 'Espera' (no puede comprar).
// La liberación del cupo y rotación de la lista corre en backend (scheduler), no acá.
const cupoExpirado = (profileData) => {
if (!profileData || profileData.estadoLista !== 'Activo') return false;
const fechaExp = aFecha(profileData.fechaExpiracion);
return fechaExp != null && fechaExp.getTime() < Date.now();
};
export const AuthProvider = ({ children }) => {
const [user, setUser] = useState(null);
const [loading, setLoading] = useState(true);
useEffect(() => {
const db = getFirestore();
const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
if (currentUser) {
try {
const clienteRef = doc(db, "clientes", currentUser.uid);
const clienteSnap = await getDoc(clienteRef);
if (clienteSnap.exists()) {
const profileData = clienteSnap.data();
if (profileData) {
profileData.estadoLista = normalizarEstadoLista(profileData.estadoLista);
if (cupoExpirado(profileData)) {
// Guard client-side: cupo vencido => se bloquea la compra tratándolo como 'Espera'.
profileData.cupoExpirado = true;
profileData.estadoLista = 'Espera';
}
}
// OJO: no usar spread ({...currentUser, ...}) acá. currentUser es una
// instancia de User de Firebase Auth — sus métodos (getIdToken, etc.)
// viven en el prototipo, no como propiedades propias, así que un
// spread los pierde y deja un objeto "plano" que rompe cualquier
// user.getIdToken() más adelante (pago, unirse a lista, etc.).
// Agregar profileData directo a la instancia preserva el prototipo.
currentUser.profileData = profileData;
setUser(currentUser);
} else {
// No es cliente: si es staff (existe en 'usuarios'), le damos un perfil
// sintético de solo-lectura para que vea el sitio como un cliente.
// No crea datos en 'clientes' ni consume cupos; el backend igual rechaza
// compras porque valida que exista clientes/{uid}.
const staffSnap = await getDoc(doc(db, "usuarios", currentUser.uid));
if (staffSnap.exists()) {
const staff = staffSnap.data() || {};
currentUser.profileData = {
nombre: staff.nombre || '',
apellido: staff.apellido || '',
correoElectronico: staff.correoElectronico || currentUser.email,
estadoLista: 'Activo',
isStaff: true,
reservas: [],
cupos_vinos: [],
};
setUser(currentUser);
} else {
setUser(currentUser);
console.warn(`Documento de cliente no encontrado para UID: ${currentUser.uid}`);
}
}
} catch (error) {
console.error("Error cargando datos de perfil del cliente:", error);
setUser(currentUser);
}
} else {
setUser(null);
}
setLoading(false);
});
return unsubscribe;
}, []);
const value = {
user,
loading,
};
return (
<AuthContext.Provider value={value}>
{!loading && children}
</AuthContext.Provider>
);
};